/**
 * Zugriff auf die Definitionen der Formatvorlagen.
 *
 * Damit lässt sich unterscheiden, ob jemand eine Überschrift nur von Hand
 * umformatiert oder tatsächlich die Formatvorlage geändert hat – genau darum
 * geht es im Modul "Formatvorlagen".
 *
 * Die dafür nötige API (`Word.Document.getStyles`) gibt es erst ab dem
 * Anforderungssatz WordApi 1.5. In älteren Word-Versionen liefert `readStyles`
 * deshalb `null`; die Prüfungen weichen dann auf einen Ersatzweg aus und
 * bewerten, ob alle Absätze derselben Ebene einheitlich formatiert sind.
 */

import { supportsWordApi, wordApi } from "./capabilities";
import type { FontState } from "./document";

export interface StyleDefinition {
  /** Angezeigter Name, in deutschem Word z. B. "Überschrift 1". */
  nameLocal: string;
  builtIn: boolean;
  /** "Paragraph", "Character", "List" oder "Table". */
  type: string;
  font: FontState;
}

/** Steht die API für Formatvorlagen in dieser Word-Version zur Verfügung? */
export function supportsStyleApi(): boolean {
  return supportsWordApi(wordApi.styles);
}

const STYLE_PROPERTIES = [
  "nameLocal",
  "builtIn",
  "type",
  "font/name",
  "font/size",
  "font/bold",
  "font/italic",
  "font/underline",
  "font/color",
  "font/highlightColor",
]
  .map((property) => `items/${property}`)
  .join(",");

function toText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Liest alle Formatvorlagen des Dokuments – oder `null`, wenn die API fehlt. */
export async function readStyles(): Promise<StyleDefinition[] | null> {
  if (!supportsStyleApi()) return null;

  try {
    return await Word.run(async (context) => {
      const styles = context.document.getStyles();
      styles.load(STYLE_PROPERTIES);
      await context.sync();

      return styles.items.map((style) => ({
        nameLocal: toText(style.nameLocal) ?? "",
        builtIn: style.builtIn === true,
        type: toText(style.type) ?? "Paragraph",
        font: {
          name: toText(style.font?.name),
          size: typeof style.font?.size === "number" ? style.font.size : null,
          bold: typeof style.font?.bold === "boolean" ? style.font.bold : null,
          italic: typeof style.font?.italic === "boolean" ? style.font.italic : null,
          underline: toText(style.font?.underline),
          color: toText(style.font?.color),
          highlightColor: toText(style.font?.highlightColor),
        },
      }));
    });
  } catch {
    // Ältere Builds melden die Unterstützung, scheitern aber am Aufruf.
    return null;
  }
}

/**
 * Sucht eine Formatvorlage über ihren angezeigten Namen. Der Name stammt aus
 * `ParagraphInfo.styleName` und ist dadurch automatisch in der Sprache der
 * jeweiligen Word-Installation.
 */
export function findStyle(
  styles: StyleDefinition[] | null,
  nameLocal: string,
): StyleDefinition | null {
  if (!styles) return null;
  const wanted = nameLocal.trim().toLowerCase();
  return styles.find((style) => style.nameLocal.trim().toLowerCase() === wanted) ?? null;
}
